import React from "react";
import { Typography, Box, Button, Container } from "@mui/material";
import { motion } from "framer-motion";
import TodoMockup from "./TodoMockup";
import { trackEvent } from "../utils/analytics";
import { colors, mono } from "../theme/tokens";

const benefits = ["Setup in seconds", "Completely free", "No learning curve"];

const HeroSection: React.FC = () => {
  const handleGetStartedClick = () => {
    trackEvent("redirect_to_app", {
      location: "hero_section",
      destination: "app",
    });
  };

  return (
    <Box
      id="hero-section"
      sx={{
        width: "100%",
        background: colors.page,
        overflow: "hidden",
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          maxWidth: "1280px",
          px: { xs: 3, md: 6 },
          pt: { xs: 8, md: 12 },
          pb: { xs: 7, md: 9 },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: { xs: 6, md: 8 },
          flexWrap: "wrap",
        }}
      >
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          sx={{ flex: "1 1 480px", minWidth: 0, maxWidth: "560px" }}
        >
          <Box
            component="span"
            sx={{
              display: "inline-block",
              background: colors.primaryTint,
              color: colors.primary,
              fontFamily: mono,
              fontSize: "12px",
              fontWeight: 600,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              px: 1.75,
              py: "7px",
              borderRadius: "999px",
              mb: 2.75,
            }}
          >
            Free to start · No credit card required
          </Box>

          <Typography
            component="h1"
            sx={{
              fontSize: { xs: "38px", sm: "46px", md: "54px" },
              fontWeight: 800,
              lineHeight: 1.08,
              letterSpacing: "-0.02em",
              color: colors.ink,
              m: 0,
              mb: 3,
            }}
          >
            The productivity app that{" "}
            <Box component="span" sx={{ color: colors.primary }}>
              actually works
            </Box>
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: "17px", md: "19px" },
              lineHeight: 1.6,
              color: colors.muted,
              mb: 4,
              maxWidth: "470px",
            }}
          >
            Simple task management, time tracking, and notes in one focused
            workspace. No overwhelming features, just what you need to get
            things done.
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 3,
              mb: 5,
              flexWrap: "wrap",
            }}
          >
            {benefits.map((benefit) => (
              <Box
                key={benefit}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <Box
                  component="span"
                  sx={{
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    background: colors.primary,
                    color: "#fff",
                    fontSize: "11px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  ✓
                </Box>
                <Typography
                  sx={{ fontSize: "14px", fontWeight: 500, color: colors.body }}
                >
                  {benefit}
                </Typography>
              </Box>
            ))}
          </Box>

          <Button
            disableElevation
            href="https://app.renoten.com/signup"
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleGetStartedClick}
            sx={{
              background: colors.primary,
              color: "#fff",
              fontWeight: 600,
              fontSize: "17px",
              textTransform: "none",
              px: 4,
              py: 2,
              borderRadius: "12px",
              boxShadow: "0 8px 24px rgba(88,80,236,0.28)",
              transition: "all 0.3s ease",
              "&:hover": {
                background: colors.primaryHover,
                transform: "translateY(-2px)",
                boxShadow: "0 12px 30px rgba(88,80,236,0.34)",
              },
            }}
          >
            Start for Free →
          </Button>

          <Typography sx={{ fontSize: "13px", color: colors.muted, mt: 3.5 }}>
            Join thousands of professionals who've simplified their workflow
          </Typography>
        </Box>

        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15, ease: "easeOut" }}
          sx={{
            flex: "1 1 420px",
            minWidth: 0,
            maxWidth: "460px",
            display: "flex",
            justifyContent: "center",
            width: "100%",
          }}
        >
          <TodoMockup />
        </Box>
      </Container>
    </Box>
  );
};

export default HeroSection;
